import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const DamagedStock = () => {
  const [records, setRecords] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState(null);

  const [form, setForm] = useState({
    product_id: '', product_name: '', quantity: '', reason: '', notes: '', date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => { fetchRecords(); fetchProducts(); }, []);
  useEffect(() => { fetchRecords(); }, [fromDate, toDate]);

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/products/enhanced`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(resp.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchRecords = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/damaged-stock`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { from_date: fromDate, to_date: toDate }
      });
      setRecords(resp.data.records || []);
      setSummary(resp.data.summary || null);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_id || !form.quantity || !form.reason) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/damaged-stock`, {
        product_id: form.product_id,
        product_name: form.product_name,
        quantity: parseFloat(form.quantity),
        reason: form.reason,
        notes: form.notes,
        date: form.date
      }, { headers: { Authorization: `Bearer ${token}` } });
      setShowModal(false);
      setForm({ product_id: '', product_name: '', quantity: '', reason: '', notes: '', date: new Date().toISOString().split('T')[0] });
      fetchRecords();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save');
    } finally { setSaving(false); }
  };

  const handleProductSelect = (e) => {
    const pid = e.target.value;
    const prod = products.find(p => p.id === pid);
    setForm({ ...form, product_id: pid, product_name: prod?.name || '' });
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div></div>;

  return (
    <div data-testid="damaged-stock-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Damaged Stock</h1>
          <p className="text-slate-500">Register and track damaged inventory</p>
        </div>
        <button onClick={() => setShowModal(true)} className="bg-gradient-to-r from-red-600 to-red-700 text-white px-6 py-2 rounded-xl font-medium hover:shadow-lg transition-all flex items-center gap-2" data-testid="add-damaged-btn">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
          Record Damaged Stock
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Total Records</p>
            <p className="text-2xl font-bold text-slate-800">{summary.total_records}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Total Damaged Qty</p>
            <p className="text-2xl font-bold text-red-600">{summary.total_quantity}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <p className="text-sm text-slate-500">Total Value Loss</p>
            <p className="text-2xl font-bold text-red-600">Rs.{(summary.total_value || 0).toFixed(2)}</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          <span className="text-slate-400">to</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Sl</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Date</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Product</th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">Qty</th>
              <th className="px-4 py-3 text-left font-medium text-slate-600">Reason</th>
              <th className="px-4 py-3 text-right font-medium text-slate-600">Value</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {records.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">No damaged stock records</td></tr>
            ) : records.map((r, i) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-4 py-3">{i + 1}</td>
                <td className="px-4 py-3 text-xs">{new Date(r.date || r.created_at).toLocaleDateString('en-IN')}</td>
                <td className="px-4 py-3 font-medium">{r.product_name}</td>
                <td className="px-4 py-3 text-right text-red-600 font-medium">{r.quantity}</td>
                <td className="px-4 py-3">
                  <span className="text-sm">{r.reason}</span>
                  {r.notes && <p className="text-xs text-slate-400 mt-0.5">{r.notes}</p>}
                </td>
                <td className="px-4 py-3 text-right font-medium text-red-600">Rs.{(r.value || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Record Damaged Stock</h3>
              <button onClick={() => setShowModal(false)} className="text-2xl text-slate-400 hover:text-slate-600">&times;</button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Product *</label>
                <select value={form.product_id} onChange={handleProductSelect} required className="w-full px-4 py-2 border border-slate-200 rounded-lg" data-testid="damaged-product-select">
                  <option value="">Select Product</option>
                  {products.filter(p => !p.is_raw_material).map(p => (
                    <option key={p.id} value={p.id}>{p.name} (Stock: {p.current_stock})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Quantity *</label>
                  <input type="number" value={form.quantity} onChange={e => setForm({...form, quantity: e.target.value})} required min="0.01" step="0.01" className="w-full px-4 py-2 border border-slate-200 rounded-lg" data-testid="damaged-qty-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Date</label>
                  <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} className="w-full px-4 py-2 border border-slate-200 rounded-lg" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Reason *</label>
                <select value={form.reason} onChange={e => setForm({...form, reason: e.target.value})} required className="w-full px-4 py-2 border border-slate-200 rounded-lg" data-testid="damaged-reason-select">
                  <option value="">Select Reason</option>
                  <option value="Expired">Expired</option>
                  <option value="Physical Damage">Physical Damage</option>
                  <option value="Water Damage">Water Damage</option>
                  <option value="Manufacturing Defect">Manufacturing Defect</option>
                  <option value="Pest Damage">Pest Damage</option>
                  <option value="Spoiled">Spoiled</option>
                  <option value="Broken">Broken</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} className="w-full px-4 py-2 border border-slate-200 rounded-lg" placeholder="Additional details..." />
              </div>
              <button type="submit" disabled={saving} className="w-full bg-red-600 text-white py-2.5 rounded-lg font-medium hover:bg-red-700 disabled:opacity-50" data-testid="save-damaged-btn">
                {saving ? 'Saving...' : 'Record Damaged Stock'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DamagedStock;
